import { Link } from "react-router-dom";
import { ArrowRight, MapPin, ShieldCheck, Award } from "lucide-react";
import Seo, { NAGPUR_AREAS, breadcrumbJsonLd } from "../components/Seo";

export default function NagpurAreasIndex() {
    return (
        <div className="min-h-screen bg-navy-900">
            <Seo
                title="Property in Nagpur — Plots, Flats & Commercial"
                description="Find verified plots, residential and commercial properties across Nagpur — Wardha Road, MIHAN, Mohgaon, Beltarodi, Jamtha, Manish Nagar, Hingna, Samruddhi Mahamarg, near Nagpur Metro & airport."
                path="/property-in-nagpur"
                keywords="property in Nagpur, plots in Nagpur, residential plots in Nagpur, commercial property in Nagpur, real estate company in Nagpur, property dealers in Nagpur, gated community plots Nagpur, NA plots Nagpur, investment property in Nagpur, plots in Maharashtra, real estate marketplace Maharashtra"
                jsonLd={breadcrumbJsonLd([
                    { name: "Home", path: "/" },
                    { name: "Property in Nagpur", path: "/property-in-nagpur" },
                ])}
            />

            <section className="text-on-image relative bg-gradient-to-br from-navy-950 to-navy-900 border-b border-gold-500/10 py-20">
                <div className="absolute inset-0 grid-pattern opacity-25" />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold-500/10 border border-gold-500/40 mb-5">
                        <Award className="w-3.5 h-3.5 text-gold-500" />
                        <span className="text-xs font-semibold text-gold-500 tracking-wider uppercase">India's Most Trusted Real Estate Ecosystem</span>
                    </div>
                    <h1 className="font-heading text-4xl lg:text-6xl font-bold text-white tracking-tight">
                        Property in <span className="text-gold-500">Nagpur</span>
                    </h1>
                    <p className="text-white/70 mt-5 max-w-3xl text-lg leading-relaxed">
                        Discover verified plots, flats, NA plots, gated community projects and commercial properties across Nagpur's most active real estate corridors. Every listing is RERA-checked and document-verified.
                    </p>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
                <div className="label-overline mb-2">Popular Areas</div>
                <h2 className="font-heading text-3xl font-bold text-white mb-8">Browse by location</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {NAGPUR_AREAS.map((a) => (
                        <Link
                            key={a.slug}
                            to={`/property-in-nagpur/${a.slug}`}
                            className="group rounded-2xl bg-navy-800 border border-gold-500/15 p-6 hover:border-gold-500/50 transition-colors"
                            data-testid={`area-card-${a.slug}`}
                        >
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 rounded-xl bg-gold-500/10 text-gold-500 flex items-center justify-center shrink-0">
                                    <MapPin className="w-5 h-5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-heading text-xl font-bold text-white group-hover:text-gold-500 transition-colors">{a.name}</div>
                                    <p className="text-sm text-white/65 mt-2 leading-relaxed">{a.desc}</p>
                                    <div className="text-xs text-gold-500/80 mt-3 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                                        Explore properties <ArrowRight className="w-3 h-3" />
                                    </div>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* Trust strip */}
            <section className="bg-navy-950 border-t border-white/5 py-12">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Strip icon={ShieldCheck} title="RERA Verified" body="Every property and broker on the platform is independently verified." />
                    <Strip icon={Award} title="Local Expertise" body="On-ground brokers, advocates and loan agents across Nagpur." />
                    <Strip icon={MapPin} title="Maharashtra Wide" body="Coverage extends to Mumbai, Pune, Aurangabad and 50+ cities." />
                </div>
            </section>
        </div>
    );
}

function Strip({ icon: Icon, title, body }) {
    return (
        <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-gold-500/10 text-gold-500 flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5" />
            </div>
            <div>
                <div className="font-heading font-bold text-white">{title}</div>
                <div className="text-sm text-white/65 mt-1">{body}</div>
            </div>
        </div>
    );
}
